/**
 * Memoket Subscribe Flow State Manager
 * Handles email subscription/unsubscription with state machine pattern
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  // State definitions
  var SubscribeStates = {
    LOADING: 'loading',
    DEFAULT: 'default',
    VERIFYING: 'verifying',
    SUBSCRIBING: 'subscribing',
    SUBSCRIBED: 'subscribed',
    ALREADY_SUBSCRIBED: 'already_subscribed',
    SUBSCRIBE_ERROR: 'subscribe_error',
    SUBSCRIBE_TIMEOUT: 'subscribe_timeout',
    MODAL_OPEN: 'modal_open',
    UNSUBSCRIBING: 'unsubscribing',
    UNSUB_ERROR: 'unsub_error',
    UNSUB_TIMEOUT: 'unsub_timeout'
  };

  /**
   * Subscribe Flow Manager Class
   */
  function SubscribeFlowManager() {
    this.currentState = SubscribeStates.LOADING;
    this.email = '';
    this.unsubscribeReason = '';
    this.retryCount = 0;

    this.config = {
      debounceDelay: 500,
      apiTimeout: 10000,
      maxRetries: 2,
      klaviyoCompanyId: '',
      klaviyoListId: '',
      metaCapiUrl: '', // Meta Conversion API Lambda URL
      metaCapiEnabled: false,
      metaCapiDebug: false
    };

    this.debounceTimer = null;
    this.forms = [];
    this.modal = null;
    this.translations = {};

    this.init();
  }

  /**
   * Initialize the manager
   */
  SubscribeFlowManager.prototype.init = function() {
    // Load config from global
    if (window.MemoketConfig) {
      this.config.klaviyoCompanyId = window.MemoketConfig.klaviyoCompanyId || '';
      this.config.klaviyoListId = window.MemoketConfig.klaviyoListId || '';
      this.translations = window.MemoketConfig.translations || {};

      // Load Meta Conversion API config
      this.config.metaCapiUrl = window.MemoketConfig.metaCapiUrl || '';
      this.config.metaCapiEnabled = window.MemoketConfig.metaCapiEnabled || false;
      this.config.metaCapiDebug = window.MemoketConfig.metaCapiDebug || false;

      if (this.config.metaCapiDebug && this.config.metaCapiEnabled) {
        console.log('[SubscribeFlow] Meta CAPI enabled:', {
          url: this.config.metaCapiUrl,
          debug: this.config.metaCapiDebug
        });
      }
    }

    this.persistUTMParams();
    this.initUTMForwarding();
    this.cacheDOMElements();
    this.checkLocalStorage();
    this.bindEvents();

    this.waitForKlaviyoSDK();
  };

  /**
   * Wait for Klaviyo SDK to load
   */
  SubscribeFlowManager.prototype.waitForKlaviyoSDK = function() {
    var checkCount = 0;
    var maxChecks = 50;

    this.transitionTo(SubscribeStates.DEFAULT);

    function checkKlaviyo() {
      checkCount++;

      if (typeof window._learnq !== 'undefined') {
        console.log('[SubscribeFlow] ✓ Klaviyo SDK loaded successfully');
        return;
      }

      if (checkCount >= maxChecks) {
        console.warn('[SubscribeFlow] Klaviyo SDK not loaded yet; continuing without onsite SDK');
        return;
      }

      setTimeout(checkKlaviyo, 100);
    }

    checkKlaviyo();
  };

  /**
   * Cache DOM elements for performance
   */
  SubscribeFlowManager.prototype.cacheDOMElements = function() {
    this.forms = Array.prototype.slice.call(
      document.querySelectorAll('[data-subscribe-form]')
    );
    this.modal = document.querySelector('[data-unsubscribe-modal]');

    // Ensure all success messages are hidden on initialization
    var successMessages = document.querySelectorAll('[data-success-message]');
    for (var i = 0; i < successMessages.length; i++) {
      var msg = successMessages[i];
      msg.classList.add('hidden');
      msg.classList.remove('block');
      msg.style.display = 'none';
      msg.style.visibility = 'hidden';
    }
  };

  /**
   * Check localStorage for existing subscription
   * Note: We no longer fill email inputs on page load - user must enter email each time
   */
  SubscribeFlowManager.prototype.checkLocalStorage = function() {
    try {
      var storedEmail = localStorage.getItem('memoket_email');
      if (storedEmail) {
        localStorage.removeItem('memoket_email');
        document.cookie = 'memoket_email=; max-age=0; path=/; SameSite=Lax';
        console.log('[SubscribeFlow] Removed legacy persisted email storage');
      }
    } catch (error) {
      console.warn('[SubscribeFlow] localStorage not available:', error);
    }
  };

  /**
   * Fill all email inputs with a value
   */
  SubscribeFlowManager.prototype.fillEmailInputs = function(email) {
    var inputs = document.querySelectorAll('[data-email-input]');
    for (var i = 0; i < inputs.length; i++) {
      inputs[i].value = email;
    }
  };

  /**
   * Bind event listeners
   */
  SubscribeFlowManager.prototype.bindEvents = function() {
    var self = this;

    // Form submissions
    document.addEventListener('submit', function(e) {
      var form = e.target.closest('[data-subscribe-form]');
      if (form) {
        e.preventDefault();
        self.handleSubscribe(form);
      }
    });

    // Email input with debounce
    document.addEventListener('input', function(e) {
      var input = e.target.closest('[data-email-input]');
      if (input) {
        clearTimeout(self.debounceTimer);
        self.debounceTimer = setTimeout(function() {
          self.syncEmailInputs(input.value);
        }, self.config.debounceDelay);
      }
    });

    // Terms checkbox change - DISABLED (feature hidden)
    /*
    document.addEventListener('change', function(e) {
      var checkbox = e.target.closest('[data-terms-checkbox]');
      if (checkbox) {
        var form = checkbox.closest('[data-subscribe-form]');
        if (form) {
          var termsError = form.querySelector('[data-terms-error]');
          if (termsError && checkbox.checked) {
            termsError.classList.add('hidden');
            termsError.classList.remove('block');
          }
        }
      }
    });
    */

    // Unsubscribe button
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-unsubscribe-btn]')) {
        e.preventDefault();
        self.openModal();
      }
    });

    // Modal actions
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-modal-keep]')) {
        self.closeModal();
        self.trackEvent('modal_action', { action: 'keep_subscribed' });
      }
      if (e.target.closest('[data-modal-confirm]')) {
        self.handleUnsubscribe();
      }
      if (e.target.closest('[data-modal-close]')) {
        self.closeModal();
      }
    });

    // Close modal on backdrop click
    if (this.modal) {
      this.modal.addEventListener('click', function(e) {
        if (e.target === self.modal) {
          self.closeModal();
        }
      });
    }

    // ESC key to close modal
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.currentState === SubscribeStates.MODAL_OPEN) {
        self.closeModal();
      }
    });
  };

  /**
   * Sync email value across all inputs
   */
  SubscribeFlowManager.prototype.syncEmailInputs = function(value) {
    var inputs = document.querySelectorAll('[data-email-input]');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].value !== value) {
        inputs[i].value = value;
      }
    }
  };

  /**
   * State transition handler
   */
  SubscribeFlowManager.prototype.transitionTo = function(newState) {
    var prevState = this.currentState;
    this.currentState = newState;

    // Enhanced logging with emojis for better visibility
    var stateEmojis = {
      'loading': '⏳',
      'default': '📝',
      'verifying': '🔍',
      'subscribing': '📤',
      'subscribed': '✅',
      'already_subscribed': 'ℹ️',
      'subscribe_error': '❌',
      'subscribe_timeout': '⏱️',
      'modal_open': '🔔',
      'unsubscribing': '📤',
      'unsub_error': '❌',
      'unsub_timeout': '⏱️'
    };

    var emoji = stateEmojis[newState] || '•';
    console.log('[SubscribeFlow] State Transition:', emoji, prevState, '→', newState);

    if (newState === 'already_subscribed') {
      console.log('[SubscribeFlow] ℹ️ User attempted to subscribe again - showing "already subscribed" message');
    }

    this.updateUI();
    this.trackStateChange(prevState, newState);

    // Dispatch custom event for sticky subscribe banner
    if (newState === SubscribeStates.SUBSCRIBED || newState === SubscribeStates.ALREADY_SUBSCRIBED) {
      var event = new CustomEvent('subscribeStateChange', {
        detail: {
          previousState: prevState,
          newState: newState,
          email: this.email
        }
      });
      document.dispatchEvent(event);
    }
  };

  /**
   * Update UI based on current state
   */
  SubscribeFlowManager.prototype.updateUI = function() {
    var self = this;

    // Update all forms
    this.forms.forEach(function(form) {
      self.updateFormUI(form);
    });

    // Update modal if exists
    if (this.modal) {
      this.updateModalUI();
    }

    // Update form wrappers state class
    var wrappers = document.querySelectorAll('[data-email-form-wrapper]');
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      // Remove all state classes
      wrapper.className = wrapper.className.replace(/\bstate-\S+/g, '').trim();
      // Add current state class
      wrapper.classList.add('state-' + this.currentState);

      // Keep large success message hidden (use small message instead)
      var successMessage = wrapper.querySelector('[data-success-message]');
      if (successMessage) {
        successMessage.classList.add('hidden');
        successMessage.classList.remove('block');
        // Force hide with inline style to override any CSS rules
        successMessage.style.display = 'none';
        successMessage.style.visibility = 'hidden';
      }
    }
  };

  /**
   * Update form UI elements
   */
  SubscribeFlowManager.prototype.updateFormUI = function(form) {
    var emailInput = form.querySelector('[data-email-input]');
    var submitBtn = form.querySelector('[data-submit-btn]');
    var unsubBtn = form.querySelector('[data-unsubscribe-btn]');
    var btnText = form.querySelector('[data-btn-text]');
    var btnSpinner = form.querySelector('[data-btn-spinner]');
    var message = form.parentElement.querySelector('[data-message]');
    var defaultButtonText = 'Subscribe NOW';

    if (!emailInput || !submitBtn) return;

    if (btnText) {
      if (!submitBtn.dataset.defaultText) {
        submitBtn.dataset.defaultText = (btnText.textContent || '').trim() || defaultButtonText;
      }

      defaultButtonText = submitBtn.dataset.defaultText || defaultButtonText;
    }

    // Reset message styling
    if (message) {
      message.className = 'mt-4 text-center text-sm min-h-0 transition-all duration-200';
      // Hide message if empty
      if (!message.textContent || message.textContent.trim() === '') {
        message.style.display = 'none';
      } else {
        message.style.display = 'block';
      }
    }

    var t = this.translations.subscribe || {};

    switch (this.currentState) {
      case SubscribeStates.LOADING:
        emailInput.disabled = true;
        submitBtn.disabled = true;
        if (btnText) btnText.textContent = 'Loading...';
        break;

      case SubscribeStates.DEFAULT:
        emailInput.disabled = false;
        emailInput.readOnly = false;
        emailInput.classList.remove('bg-gray-100', 'text-gray-500');
        submitBtn.disabled = false;
        submitBtn.classList.remove('hidden');
        if (btnText) btnText.textContent = defaultButtonText;
        if (btnSpinner) btnSpinner.classList.add('hidden');
        if (unsubBtn) unsubBtn.classList.add('hidden');
        if (message) {
          message.textContent = '';
          message.style.display = 'none';
        }
        break;

      case SubscribeStates.VERIFYING:
        emailInput.disabled = true;
        submitBtn.disabled = true;
        if (btnText) btnText.textContent = 'Verifying...';
        break;

      case SubscribeStates.SUBSCRIBING:
        emailInput.disabled = true;
        submitBtn.disabled = true;
        if (btnText) btnText.textContent = 'Sending...';
        if (btnSpinner) btnSpinner.classList.remove('hidden');
        break;

      case SubscribeStates.SUBSCRIBED:
        // Keep form visible and inputs enabled (per user request)
        emailInput.disabled = false;
        emailInput.readOnly = false;
        emailInput.value = ''; // Clear input after success
        submitBtn.disabled = false;
        if (btnText) btnText.textContent = defaultButtonText;
        if (btnSpinner) btnSpinner.classList.add('hidden');

        // Hide inline success message (modal will handle success display)
        if (message) {
          message.textContent = '';
          message.style.display = 'none';
        }
        break;

      case SubscribeStates.ALREADY_SUBSCRIBED:
        // Keep form visible and inputs enabled (per user request)
        emailInput.disabled = false;
        emailInput.readOnly = false;
        emailInput.value = ''; // Clear input after success
        submitBtn.disabled = false;
        if (btnText) btnText.textContent = defaultButtonText;
        if (btnSpinner) btnSpinner.classList.add('hidden');

        // Hide inline success message (modal will handle success display)
        if (message) {
          message.textContent = '';
          message.style.display = 'none';
        }

        // Transition to subscribed state after a short delay
        var self = this;
        setTimeout(function() {
          self.transitionTo(SubscribeStates.SUBSCRIBED);
        }, 100);
        break;

      case SubscribeStates.SUBSCRIBE_ERROR:
        emailInput.disabled = false;
        submitBtn.disabled = false;
        if (btnText) btnText.textContent = 'Try Again';
        if (btnSpinner) btnSpinner.classList.add('hidden');
        if (message) {
          // Check if the error was likely caused by an ad blocker
          var manager = window.subscribeFlow;
          var isBlocked = manager && manager._lastSubscribeErrorIsBlocked;
          if (isBlocked) {
            message.textContent = t.errorBlocked || 'Subscription failed. Please disable your ad blocker and try again.';
          } else {
            message.textContent = t.error || 'Something went wrong. Please try again.';
          }
          message.classList.add('text-red-600');
          message.style.display = 'block';
        }
        break;

      case SubscribeStates.SUBSCRIBE_TIMEOUT:
        emailInput.disabled = false;
        submitBtn.disabled = false;
        if (btnText) btnText.textContent = 'Resend';
        if (btnSpinner) btnSpinner.classList.add('hidden');
        if (message) {
          message.textContent = t.timeout || 'Request timed out. Please try again.';
          message.classList.add('text-orange-600');
          message.style.display = 'block';
        }
        break;
    }
  };

  /**
   * Update modal UI elements
   */
  SubscribeFlowManager.prototype.updateModalUI = function() {
    if (!this.modal) return;

    var confirmBtn = this.modal.querySelector('[data-modal-confirm]');
    var confirmText = this.modal.querySelector('[data-confirm-text]');
    var confirmSpinner = this.modal.querySelector('[data-confirm-spinner]');
    var message = this.modal.querySelector('[data-modal-message]');
    var modalContent = this.modal.querySelector('[data-modal-content]');

    var t = this.translations.modal || {};

    switch (this.currentState) {
      case SubscribeStates.MODAL_OPEN:
        this.modal.classList.remove('opacity-0', 'invisible', 'pointer-events-none');
        this.modal.classList.add('opacity-100', 'visible', 'pointer-events-auto');
        if (modalContent) {
          modalContent.classList.remove('scale-95');
          modalContent.classList.add('scale-100');
        }
        if (confirmBtn) confirmBtn.disabled = false;
        if (confirmText) confirmText.textContent = 'Confirm Unsubscribe';
        if (confirmSpinner) confirmSpinner.classList.add('hidden');
        if (message) {
          message.textContent = '';
          message.style.display = 'none';
        }
        break;

      case SubscribeStates.UNSUBSCRIBING:
        if (confirmBtn) confirmBtn.disabled = true;
        if (confirmText) confirmText.textContent = 'Processing...';
        if (confirmSpinner) confirmSpinner.classList.remove('hidden');
        break;

      case SubscribeStates.UNSUB_ERROR:
        if (confirmBtn) confirmBtn.disabled = false;
        if (confirmText) confirmText.textContent = 'Try Again';
        if (confirmSpinner) confirmSpinner.classList.add('hidden');
        if (message) {
          message.textContent = t.error || 'Something went wrong. Please try again.';
          message.classList.add('text-red-600');
          message.style.display = 'block';
        }
        break;

      case SubscribeStates.DEFAULT:
      case SubscribeStates.SUBSCRIBED:
        this.modal.classList.add('opacity-0', 'invisible', 'pointer-events-none');
        this.modal.classList.remove('opacity-100', 'visible', 'pointer-events-auto');
        if (modalContent) {
          modalContent.classList.add('scale-95');
          modalContent.classList.remove('scale-100');
        }
        break;
    }
  };

  /**
   * Get UTM parameters from URL or sessionStorage.
   * On the landing page the URL carries UTM query-strings; we persist them
   * to sessionStorage so they survive internal navigations (product → cart
   * → checkout) within the same browser session.
   *
   * Priority: URL params (freshest) → sessionStorage (persisted earlier).
   * If the URL contains new UTM params they overwrite the stored values.
   */
  SubscribeFlowManager.prototype.getUTMParams = function() {
    var utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    try {
      var params = new URLSearchParams(window.location.search);
      var hasURLUTM = utmKeys.some(function(k) { return !!params.get(k); });

      if (hasURLUTM) {
        var result = {};
        utmKeys.forEach(function(k) { result[k] = params.get(k) || ''; });
        // Persist to sessionStorage so subsequent pages can read them
        try { sessionStorage.setItem('memoket_utm_params', JSON.stringify(result)); } catch (_) {}
        return result;
      }

      // Fallback: read from sessionStorage (set on the landing page)
      var stored = sessionStorage.getItem('memoket_utm_params');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('[SubscribeFlow] Could not get UTM params:', e);
    }
    return {};
  };

  /**
   * Persist UTM parameters as early as possible so attribution survives
   * consent-gated analytics loading and pre-subscription page navigation.
   */
  SubscribeFlowManager.prototype.persistUTMParams = function() {
    this.getUTMParams();
  };

  /**
   * Append persisted UTM params to same-origin links and forms before navigation.
   * This mirrors the analytics.js behavior, but subscribe-flow.js is always loaded
   * on pages with email capture, independent of analytics consent.
   */
  SubscribeFlowManager.prototype.initUTMForwarding = function() {
    var self = this;

    if (window.__memoketSubscribeUTMForwardingInitialized) return;
    window.__memoketSubscribeUTMForwardingInitialized = true;

    function appendUTM(href) {
      try {
        var utmParams = self.getUTMParams();
        if (!utmParams.utm_source) return href;

        var url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return href;
        if (url.searchParams.has('utm_source')) return href;
        if (/\.(js|css|png|jpe?g|gif|svg|webp|mp4|webm|woff2?|ttf|eot|ico)(\?|$)/i.test(url.pathname)) return href;

        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function(key) {
          if (utmParams[key]) url.searchParams.set(key, utmParams[key]);
        });

        return url.toString();
      } catch (error) {
        return href;
      }
    }

    document.addEventListener('click', function(event) {
      var anchor = event.target.closest('a[href]');
      if (!anchor) return;

      var href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      var newHref = appendUTM(href);
      if (newHref !== href) {
        anchor.setAttribute('href', newHref);
      }
    }, true);

    document.addEventListener('submit', function(event) {
      var form = event.target.closest('form');
      if (!form) return;

      var action = form.getAttribute('action');
      if (!action) return;

      var newAction = appendUTM(action);
      if (newAction !== action) {
        form.setAttribute('action', newAction);
      }
    }, true);
  };

  /**
   * Check if test mode is enabled via URL parameter
   */
  SubscribeFlowManager.prototype.isTestMode = function() {
    try {
      var params = new URLSearchParams(window.location.search);
      return params.get('mode') === 'test';
    } catch (e) {
      return false;
    }
  };

  /**
   * Check if debug mode is enabled via URL parameter (debug=true)
   * In debug mode, skip API calls and show success modal directly
   */
  SubscribeFlowManager.prototype.isDebugMode = function() {
    try {
      var params = new URLSearchParams(window.location.search);
      return params.get('debug') === 'true';
    } catch (e) {
      return false;
    }
  };

  /**
   * Validate email format
   */
  SubscribeFlowManager.prototype.validateEmail = function(email) {
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Handle subscribe form submission
   */
  SubscribeFlowManager.prototype.handleSubscribe = function(form) {
    var self = this;
    var emailInput = form.querySelector('[data-email-input]');
    var email = emailInput.value.trim();
    var message = form.parentElement.querySelector('[data-message]');
    var formId = form.dataset.subscribeForm;

    // Check for terms checkbox - DISABLED (feature hidden)
    /*
    var termsCheckbox = form.querySelector('[data-terms-checkbox]');
    var termsError = form.querySelector('[data-terms-error]');
    */

    // Validate email
    if (!this.validateEmail(email)) {
      if (message) {
        var t = this.translations.subscribe || {};
        message.textContent = t.invalidEmail || 'Please enter a valid email address';
        message.classList.add('text-red-600');
        message.style.display = 'block';
      }
      emailInput.focus();
      return;
    }

    // Validate terms agreement (if checkbox exists) - DISABLED (feature hidden)
    /*
    if (termsCheckbox && !termsCheckbox.checked) {
      if (termsError) {
        termsError.classList.remove('hidden');
        termsError.classList.add('block');
      }
      // Scroll to the checkbox if needed
      termsCheckbox.focus();
      return;
    }

    // Hide terms error if validation passes
    if (termsError) {
      termsError.classList.add('hidden');
      termsError.classList.remove('block');
    }
    */

    this.email = email;

    // Debug mode: skip API call and show success modal directly
    if (this.isDebugMode()) {
      console.log('[SubscribeFlow] 🔧 DEBUG MODE - Skipping API call, showing success modal directly');
      console.log('[SubscribeFlow] Debug info:', { email: email, section: formId });

      this.transitionTo(SubscribeStates.SUBSCRIBING);

      // Simulate brief loading state for better UX
      setTimeout(function() {
        console.log('[SubscribeFlow] 🔧 DEBUG MODE - Triggering success state');
        self.transitionTo(SubscribeStates.SUBSCRIBED);
      }, 500);

      return;
    }

    // Reset retry count for new submission
    this.retryCount = 0;
    this.transitionTo(SubscribeStates.SUBSCRIBING);

    // Call API with retry support
    this.attemptSubscribe(email, formId);
  };

  /**
   * Attempt subscribe with automatic retry on failure
   */
  SubscribeFlowManager.prototype.attemptSubscribe = function(email, formId) {
    var self = this;

    this.apiSubscribe(email, formId)
      .then(function(response) {
        // Reset retry count on success
        self.retryCount = 0;

        if (response.status === 'success') {
          console.log('[SubscribeFlow] ✓ Subscription successful for:', email);
          self.saveToLocalStorage(email, true);
          self.transitionTo(SubscribeStates.SUBSCRIBED);
          self.trackEvent('subscribe_success', { section: formId });
        } else if (response.status === 'already_subscribed') {
          console.log('[SubscribeFlow] ℹ User already subscribed:', email);
          console.log('[SubscribeFlow] Display status: Already subscribed, showing confirmation message');
          self.saveToLocalStorage(email, true);
          self.transitionTo(SubscribeStates.ALREADY_SUBSCRIBED);
          self.trackEvent('already_subscribed_attempt', { section: formId });
        }
      })
      .catch(function(error) {
        console.error('[SubscribeFlow] ✗ Subscribe error (attempt ' + (self.retryCount + 1) + '/' + (self.config.maxRetries + 1) + '):', error);
        console.error('[SubscribeFlow] Error details:', {
          email: email,
          section: formId,
          error: error.message,
          attempt: self.retryCount + 1,
          maxRetries: self.config.maxRetries
        });

        // Check if we can retry
        if (self.retryCount < self.config.maxRetries && error.message !== 'timeout') {
          self.retryCount++;
          var retryDelay = Math.min(1000 * Math.pow(2, self.retryCount - 1), 4000); // Exponential backoff: 1s, 2s, 4s
          console.log('[SubscribeFlow] 🔄 Retrying in ' + retryDelay + 'ms (attempt ' + (self.retryCount + 1) + '/' + (self.config.maxRetries + 1) + ')');

          setTimeout(function() {
            self.attemptSubscribe(email, formId);
          }, retryDelay);
        } else {
          // All retries exhausted or timeout
          if (error.message === 'timeout') {
            self.transitionTo(SubscribeStates.SUBSCRIBE_TIMEOUT);
          } else {
            // Detect if the error is likely caused by an ad blocker
            var isBlockedError = self.isLikelyAdBlocker(error);
            if (isBlockedError) {
              console.warn('[SubscribeFlow] ⚠️ Request appears to be blocked by ad blocker or privacy extension');
            }
            self._lastSubscribeError = error;
            self._lastSubscribeErrorIsBlocked = isBlockedError;
            self.transitionTo(SubscribeStates.SUBSCRIBE_ERROR);
          }
          self.retryCount = 0;
          self.trackEvent('subscribe_error', { error: error.message, section: formId, retries: self.config.maxRetries });
        }
      });
  };

  /**
   * Detect if an error is likely caused by an ad blocker or privacy extension
   */
  SubscribeFlowManager.prototype.isLikelyAdBlocker = function(error) {
    if (!error) return false;
    var msg = (error.message || '').toLowerCase();
    // Common ad-blocker / network-blocked error patterns
    return (
      msg.indexOf('failed to fetch') !== -1 ||
      msg.indexOf('networkerror') !== -1 ||
      msg.indexOf('network request failed') !== -1 ||
      msg.indexOf('load failed') !== -1 ||
      msg.indexOf('blocked') !== -1 ||
      msg.indexOf('err_blocked') !== -1 ||
      msg === 'failed to fetch'
    );
  };

  SubscribeFlowManager.prototype.isAlreadySubscribedResponse = function(data, text) {
    var combined = (text || '').toLowerCase();

    try {
      combined += ' ' + JSON.stringify(data || {}).toLowerCase();
    } catch (error) {}

    return (
      (combined.indexOf('already') !== -1 && combined.indexOf('subscrib') !== -1) ||
      (combined.indexOf('duplicate') !== -1 && combined.indexOf('subscrib') !== -1)
    );
  };

  /**
   * API call to subscribe
   */
  SubscribeFlowManager.prototype.apiSubscribe = function(email, section) {
    var self = this;

    return new Promise(function(resolve, reject) {
      // Check for test mode - skip actual API calls
      if (self.isTestMode()) {
        console.log('[SubscribeFlow] 🧪 TEST MODE ENABLED - Simulating successful subscription');
        console.log('[SubscribeFlow] Test mode: Skipping Klaviyo API calls');
        console.log('[SubscribeFlow] Test mode: Email:', email);
        console.log('[SubscribeFlow] Test mode: Section:', section);

        // Simulate API delay (200ms)
        setTimeout(function() {
          console.log('[SubscribeFlow] 🧪 TEST MODE: Returning success response');
          resolve({ status: 'success' });
        }, 200);
        return;
      }

      var timeoutId = setTimeout(function() {
        console.error('[SubscribeFlow] ⏱ API request timeout after', self.config.apiTimeout, 'ms');
        reject(new Error('timeout'));
      }, self.config.apiTimeout);

      // Check if Klaviyo configuration is available
      console.log('[SubscribeFlow] Checking Klaviyo configuration...', {
        hasLearnq: typeof window._learnq !== 'undefined',
        listId: self.config.klaviyoListId,
        companyId: self.config.klaviyoCompanyId
      });

      if (self.config.klaviyoListId && self.config.klaviyoCompanyId) {
        console.log('[SubscribeFlow] ✓ Klaviyo configured, processing subscription for:', email);
        console.log('[SubscribeFlow] Configuration:', {
          listId: self.config.klaviyoListId,
          companyId: self.config.klaviyoCompanyId,
          section: section
        });

        // Get UTM parameters for attribution
        var utmParams = self.getUTMParams();
        console.log('[SubscribeFlow] UTM Parameters:', utmParams);

        // Step 1: Identify the user using _learnq (if available)
        if (typeof window._learnq !== 'undefined') {
          console.log('[SubscribeFlow] Step 1/3: Identifying user profile via _learnq...');

          // Prepare user properties with UTM params using Klaviyo's standard attribution property names
          var userProperties = {
            '$email': email,
            // Klaviyo standard attribution fields (these populate "How they found you" section)
            '$source': utmParams.utm_source || document.referrer || 'direct',
            '$medium': utmParams.utm_medium || '',
            '$campaign': utmParams.utm_campaign || '',
            '$content': utmParams.utm_content || '',
            '$term': utmParams.utm_term || '',
            '$referrer': document.referrer || '',
            '$first_page': window.location.href
          };

          // Also store as initial UTM params (for first-touch attribution)
          if (utmParams.utm_source) {
            userProperties['$initial_utm_source'] = utmParams.utm_source;
            userProperties['$initial_utm_medium'] = utmParams.utm_medium || '';
            userProperties['$initial_utm_campaign'] = utmParams.utm_campaign || '';
            userProperties['$initial_utm_content'] = utmParams.utm_content || '';
            userProperties['$initial_utm_term'] = utmParams.utm_term || '';
            userProperties['$initial_referrer'] = document.referrer || '';
            userProperties['$initial_referring_domain'] = document.referrer ? new URL(document.referrer).hostname : '';
          }

          // Also keep custom properties for easy access in segments/flows
          if (utmParams.utm_source) userProperties.utm_source = utmParams.utm_source;
          if (utmParams.utm_medium) userProperties.utm_medium = utmParams.utm_medium;
          if (utmParams.utm_campaign) userProperties.utm_campaign = utmParams.utm_campaign;
          if (utmParams.utm_content) userProperties.utm_content = utmParams.utm_content;
          if (utmParams.utm_term) userProperties.utm_term = utmParams.utm_term;

          // Track subscribe source for segmentation
          userProperties.subscribe_source = (section === 'popup') ? 'popup_form' : 'inline_form';

          // Remove empty values to keep profile clean
          Object.keys(userProperties).forEach(function(key) {
            if (userProperties[key] === '' || userProperties[key] === null || userProperties[key] === undefined) {
              delete userProperties[key];
            }
          });

          console.log('[SubscribeFlow] User properties for Klaviyo:', userProperties);

          window._learnq.push(['identify', userProperties]);

          // Step 2: Track subscription event with UTM params
          console.log('[SubscribeFlow] Step 2/3: Tracking subscription event...');

          var eventProperties = {
            source: 'landing_page',
            section: section,
            subscribe_source: (section === 'popup') ? 'popup_form' : 'inline_form',
            page_url: window.location.href,
            referrer: document.referrer || 'direct'
          };

          // Add UTM params to event if they exist
          if (utmParams.utm_source) eventProperties.utm_source = utmParams.utm_source;
          if (utmParams.utm_medium) eventProperties.utm_medium = utmParams.utm_medium;
          if (utmParams.utm_campaign) eventProperties.utm_campaign = utmParams.utm_campaign;
          if (utmParams.utm_content) eventProperties.utm_content = utmParams.utm_content;
          if (utmParams.utm_term) eventProperties.utm_term = utmParams.utm_term;

          // Also add Klaviyo standard event attribution
          if (utmParams.utm_source) eventProperties['$source'] = utmParams.utm_source;
          if (utmParams.utm_medium) eventProperties['$medium'] = utmParams.utm_medium;
          if (utmParams.utm_campaign) eventProperties['$campaign'] = utmParams.utm_campaign;

          console.log('[SubscribeFlow] Event properties:', eventProperties);

          window._learnq.push(['track', 'Subscribed to Pre-launch', eventProperties]);
        } else {
          console.log('[SubscribeFlow] _learnq not available, skipping identify/track');
        }

        // Step 3: Subscribe to List using Klaviyo Client API (recommended method)
        // Note: Wait a moment for identify to be processed before making API call
        console.log('[SubscribeFlow] Step 3/3: Adding to email list via Client API:', self.config.klaviyoListId);
        console.log('[SubscribeFlow] Waiting 500ms for identify to be processed...');

        setTimeout(function() {
          // Use Klaviyo's Client API endpoint (CORS-safe)
          var subscribeUrl = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + self.config.klaviyoCompanyId;

          console.log('[SubscribeFlow] Using Klaviyo Client API:', subscribeUrl);

          // Build custom_source with UTM info for tracking in Klaviyo
          var customSource = 'Landing Page - ' + section;
          if (utmParams.utm_source) {
            customSource += ' | ' + utmParams.utm_source;
            if (utmParams.utm_campaign) {
              customSource += ' / ' + utmParams.utm_campaign;
            }
          }

          // Prepare properties with UTM params
          // Note: While Client API may not save all properties to profile,
          // they are still useful for Klaviyo's internal processing
          var profileProperties = {
            source: 'landing_page',
            section: section,
            // Track subscribe source for segmentation in Klaviyo
            subscribe_source: (section === 'popup') ? 'popup_form' : 'inline_form',
            // Klaviyo standard fields
            '$source': utmParams.utm_source || 'direct',
            '$medium': utmParams.utm_medium || '',
            '$campaign': utmParams.utm_campaign || '',
            '$content': utmParams.utm_content || '',
            '$term': utmParams.utm_term || '',
            '$referrer': document.referrer || '',
            '$first_page': window.location.href
          };

          // Also add plain UTM params
          if (utmParams.utm_source) profileProperties.utm_source = utmParams.utm_source;
          if (utmParams.utm_medium) profileProperties.utm_medium = utmParams.utm_medium;
          if (utmParams.utm_campaign) profileProperties.utm_campaign = utmParams.utm_campaign;
          if (utmParams.utm_content) profileProperties.utm_content = utmParams.utm_content;
          if (utmParams.utm_term) profileProperties.utm_term = utmParams.utm_term;

          // Remove empty values
          Object.keys(profileProperties).forEach(function(key) {
            if (profileProperties[key] === '' || profileProperties[key] === null || profileProperties[key] === undefined) {
              delete profileProperties[key];
            }
          });

          var requestBody = {
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email,
                      properties: profileProperties
                    }
                  }
                },
                custom_source: customSource
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: self.config.klaviyoListId
                  }
                }
              }
            }
          };

          console.log('[SubscribeFlow] Request body:', JSON.stringify(requestBody, null, 2));

          fetch(subscribeUrl, {
            method: 'POST',
            headers: {
              'Accept': 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              'revision': '2026-04-15'
            },
            body: JSON.stringify(requestBody)
          })
          .then(function(response) {
            console.log('[SubscribeFlow] API Response status:', response.status);
            clearTimeout(timeoutId);

            if (response.ok || response.status === 202) {
              console.log('[SubscribeFlow] ✓ Klaviyo Client API subscription successful');
              resolve({ status: 'success' });
            } else if (response.status === 400) {
              // Could be already subscribed or validation error
              // Safely parse JSON with fallback
              return response.text().then(function(text) {
                var data = null;
                try {
                  data = JSON.parse(text);
                  console.log('[SubscribeFlow] API returned 400:', data);
                } catch (parseErr) {
                  console.log('[SubscribeFlow] API returned 400 (non-JSON):', text);
                }

                if (self.isAlreadySubscribedResponse(data, text)) {
                  resolve({ status: 'already_subscribed' });
                  return;
                }

                reject(new Error('API validation error: ' + response.status));
              });
            } else {
              return response.text().then(function(text) {
                console.error('[SubscribeFlow] API error response:', text);
                reject(new Error('API error: ' + response.status));
              });
            }
          })
          .catch(function(error) {
            console.error('[SubscribeFlow] Fetch error:', error);
            clearTimeout(timeoutId);
            reject(error);
          });
        }, 500); // End of setTimeout - wait 500ms for identify to be processed

      } else {
        // Klaviyo SDK not available
        console.error('[SubscribeFlow] ⚠️ Klaviyo SDK not available');
        console.error('[SubscribeFlow] ✗ Cannot subscribe without Klaviyo SDK');
        console.error('[SubscribeFlow] Please check:');
        console.error('[SubscribeFlow]   1. Klaviyo Company ID is set in theme settings');
        console.error('[SubscribeFlow]   2. Klaviyo script is loading (check Network tab)');
        console.error('[SubscribeFlow]   3. No ad blockers are blocking Klaviyo');

        clearTimeout(timeoutId);
        reject(new Error('Klaviyo SDK not available. Please check console for details.'));
      }
    });
  };

  /**
   * Open unsubscribe modal
   */
  SubscribeFlowManager.prototype.openModal = function() {
    this.transitionTo(SubscribeStates.MODAL_OPEN);
    this.trackEvent('modal_open', { type: 'unsubscribe' });

    // Focus first focusable element
    var self = this;
    setTimeout(function() {
      var firstFocusable = self.modal.querySelector('select, button');
      if (firstFocusable) firstFocusable.focus();
    }, 100);
  };

  /**
   * Close unsubscribe modal
   */
  SubscribeFlowManager.prototype.closeModal = function() {
    this.transitionTo(SubscribeStates.SUBSCRIBED);
  };

  /**
   * Handle unsubscribe confirmation
   */
  SubscribeFlowManager.prototype.handleUnsubscribe = function() {
    var self = this;
    var reasonSelect = this.modal.querySelector('[data-reason-select]');
    var reasonInput = this.modal.querySelector('[data-reason-input]');
    var message = this.modal.querySelector('[data-modal-message]');

    var reason = reasonSelect ? reasonSelect.value : '';
    var reasonText = (reason === 'other' && reasonInput) ? reasonInput.value : '';

    // Validate reason selection
    if (!reason) {
      if (message) {
        message.textContent = 'Please select a reason';
        message.classList.add('text-red-600');
        message.style.display = 'block';
      }
      return;
    }

    this.unsubscribeReason = reason;
    this.transitionTo(SubscribeStates.UNSUBSCRIBING);

    // Call API
    this.apiUnsubscribe(this.email, reason, reasonText)
      .then(function(response) {
        if (response.status === 'success') {
          self.clearLocalStorage();
          self.transitionTo(SubscribeStates.DEFAULT);
          self.trackEvent('unsubscribe_success', {
            reason: reason,
            reason_text: reasonText
          });
        }
      })
      .catch(function(error) {
        console.error('[SubscribeFlow] Unsubscribe error:', error);
        if (error.message === 'timeout') {
          self.transitionTo(SubscribeStates.UNSUB_TIMEOUT);
        } else {
          self.transitionTo(SubscribeStates.UNSUB_ERROR);
        }
        self.trackEvent('unsubscribe_error', { error: error.message });
      });
  };

  /**
   * API call to unsubscribe
   * Note: Due to CORS restrictions, Klaviyo's unsubscribe API cannot be called directly from the browser.
   * This implementation tracks the unsubscribe intent and clears local state.
   * Users will need to use the unsubscribe link in emails for actual list removal.
   */
  SubscribeFlowManager.prototype.apiUnsubscribe = function(email, reason, reasonText) {
    var self = this;

    return new Promise(function(resolve, reject) {
      var timeoutId = setTimeout(function() {
        reject(new Error('timeout'));
      }, self.config.apiTimeout);

      console.log('[SubscribeFlow] Processing unsubscribe request:', {
        email: email,
        listId: self.config.klaviyoListId,
        reason: reason
      });

      // Track unsubscribe intent in Klaviyo for analytics
      if (window._learnq) {
        console.log('[SubscribeFlow] Tracking unsubscribe intent event');
        window._learnq.push(['track', 'Unsubscribe Intent', {
          unsubscribe_reason: reason,
          unsubscribe_reason_text: reasonText,
          source: 'landing_page',
          list_id: self.config.klaviyoListId
        }]);
      }

      // Try to call server-side unsubscribe endpoint if available
      if (self.config.unsubscribeEndpoint) {
        console.log('[SubscribeFlow] Calling server-side unsubscribe endpoint');

        fetch(self.config.unsubscribeEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            list_id: self.config.klaviyoListId,
            company_id: self.config.klaviyoCompanyId,
            reason: reason,
            reason_text: reasonText
          })
        })
        .then(function(response) {
          clearTimeout(timeoutId);
          if (response.ok) {
            console.log('[SubscribeFlow] Server-side unsubscribe successful');
            resolve({ status: 'success', method: 'server' });
          } else {
            console.warn('[SubscribeFlow] Server-side unsubscribe failed, clearing local state only');
            resolve({ status: 'success', method: 'local' });
          }
        })
        .catch(function(error) {
          console.error('[SubscribeFlow] Server-side unsubscribe error:', error);
          clearTimeout(timeoutId);
          // Still resolve as success - we'll clear local state
          resolve({ status: 'success', method: 'local' });
        });

        return;
      }

      // Fallback: Clear local state only
      // Note: User will remain subscribed in Klaviyo until they use the email unsubscribe link
      console.warn('[SubscribeFlow] No server-side unsubscribe endpoint configured');
      console.warn('[SubscribeFlow] Clearing local state only - user will remain in Klaviyo list');
      console.warn('[SubscribeFlow] User can unsubscribe via email link');

      // Simulate brief processing time for better UX
      setTimeout(function() {
        clearTimeout(timeoutId);
        resolve({
          status: 'success',
          method: 'local',
          message: 'Local state cleared. You will remain on the list until using an email unsubscribe link.'
        });
      }, 800);
    });
  };

  /**
   * Verify subscription status on page load
   * Note: We don't show "Thanks for subscribing" message on page load,
   * only when user actually subscribes in the current session
   */
  SubscribeFlowManager.prototype.verifySubscriptionStatus = function() {
    var self = this;

    try {
      var isSubscribed = localStorage.getItem('memoket_subscribed') === 'true';
      var subscribedAt = localStorage.getItem('memoket_subscribed_at');

      console.log('[SubscribeFlow] Verifying subscription status:', {
        isSubscribed: isSubscribed,
        subscribedAt: subscribedAt || 'never'
      });

      // Always transition to DEFAULT state on page load, even if user is subscribed
      // This prevents showing "Thanks for subscribing" message on page load
      // The message will only show when user actually subscribes in the current session
      console.log('[SubscribeFlow] Transitioning to DEFAULT state (message will not show on page load)');
      this.transitionTo(SubscribeStates.DEFAULT);
    } catch (error) {
      console.warn('[SubscribeFlow] Error verifying subscription:', error);
      this.transitionTo(SubscribeStates.DEFAULT);
    }
  };

  /**
   * Save to localStorage
   */
  SubscribeFlowManager.prototype.saveToLocalStorage = function(email, subscribed) {
    try {
      var timestamp = new Date().toISOString();

      localStorage.setItem('memoket_subscribed', subscribed.toString());
      localStorage.setItem('memoket_subscribed_at', timestamp);
      localStorage.removeItem('memoket_email');

      console.log('[SubscribeFlow] 💾 Saved subscription state to localStorage:', {
        subscribed: subscribed,
        timestamp: timestamp
      });

      document.cookie = 'memoket_email=; max-age=0; path=/; SameSite=Lax';
    } catch (error) {
      console.warn('[SubscribeFlow] ⚠️ Could not save to localStorage:', error);
    }
  };

  /**
   * Clear localStorage
   */
  SubscribeFlowManager.prototype.clearLocalStorage = function() {
    try {
      localStorage.removeItem('memoket_email');
      localStorage.removeItem('memoket_subscribed');
      localStorage.removeItem('memoket_subscribed_at');

      console.log('[SubscribeFlow] 🗑️ Cleared subscription data from localStorage');

      document.cookie = 'memoket_email=; max-age=0; path=/; SameSite=Lax';

      // Clear email inputs
      var inputs = document.querySelectorAll('[data-email-input]');
      for (var i = 0; i < inputs.length; i++) {
        inputs[i].value = '';
      }

      this.email = '';
      console.log('[SubscribeFlow] ✓ All subscription state cleared');
    } catch (error) {
      console.warn('[SubscribeFlow] ⚠️ Could not clear localStorage:', error);
    }
  };

  /**
   * Track analytics event
   */
  SubscribeFlowManager.prototype.trackEvent = function(eventName, params) {
    params = params || {};

    if (window.MemoketAnalytics) {
      var analyticsParams = params;
      if (eventName === 'subscribe_success') {
        analyticsParams = {};
        for (var key in params) {
          if (params.hasOwnProperty(key)) analyticsParams[key] = params[key];
        }
        analyticsParams._suppress_meta = true;
      }

      window.MemoketAnalytics.track(eventName, analyticsParams);
    }

    // GA4: 订阅成功用 generate_lead（推荐事件），便于在 GA4 中直接看「订阅成功人数」
    if (eventName === 'subscribe_success' && window.gtag) {
      try {
        window.gtag('event', 'generate_lead', {
          currency: 'USD',
          value: 0,
          method: params && params.section ? params.section : 'newsletter'
        });
      } catch (e) {
        console.warn('[SubscribeFlow] GA4 generate_lead error:', e);
      }
    }

    // GTM: plain dataLayer event for agency-managed tags (e.g. X Ads Lead).
    // gtag('event', ...) above is not reliably visible to GTM custom-event
    // triggers, so push the message-object form too. No PII on purpose —
    // every tag in the container can read the dataLayer.
    if (eventName === 'subscribe_success') {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'subscribe_success',
          form_section: params && params.section ? params.section : 'newsletter'
        });
      } catch (e) {
        console.warn('[SubscribeFlow] dataLayer subscribe_success error:', e);
      }
    }

    // Enhanced Meta Pixel tracking for key conversion events
    if (window.fbq && eventName === 'subscribe_success') {
      try {
        // Generate unique event ID for Conversions API deduplication
        var eventId = eventName + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

        // Get fbp and fbc cookies for better matching
        var fbp = this.getCookie('_fbp') || '';
        var fbc = this.getCookie('_fbc') || '';

        // If fbc is not set but fbclid is in URL, construct it
        if (!fbc) {
          var urlParams = new URLSearchParams(window.location.search);
          var fbclid = urlParams.get('fbclid');
          if (fbclid) {
            fbc = 'fb.1.' + Date.now() + '.' + fbclid;
          }
        }

        var pixelParams = {
          content_name: 'Newsletter Subscription',
          content_category: params.section || 'landing_page',
          value: 0,
          currency: 'USD',
          // Store these for potential server-side sending
          event_id: eventId,
          fbp: fbp,
          fbc: fbc
        };

        // Track as Subscribe event
        window.fbq('track', 'Subscribe', pixelParams, {eventID: eventId});

        console.log('[SubscribeFlow] Meta Pixel events sent:', {
          eventName: eventName,
          eventId: eventId,
          hasFbp: !!fbp,
          hasFbc: !!fbc
        });

      // Store event data for potential Conversions API call
      this.lastConversionEvent = {
        event_name: 'Subscribe',
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        email: this.email,
        fbp: fbp,
        fbc: fbc,
        url: window.location.href,
        section: params.section
      };

      // ========================================
      // Send to Meta Conversion API (server-side)
      // ========================================
      if (eventName === 'subscribe_success' && this.config.metaCapiEnabled && this.config.metaCapiUrl) {
        var utmParams = this.getUTMParams();

        this.sendToConversionAPI({
          email: this.email,
          event_name: 'Subscribe',
          event_id: eventId,
          event_time: Math.floor(Date.now() / 1000),
          fbp: fbp,
          fbc: fbc,
          url: window.location.href,
          section: params.section,
          user_agent: navigator.userAgent,
          // Add UTM parameters for attribution
          utm_source: utmParams.utm_source || '',
          utm_medium: utmParams.utm_medium || '',
          utm_campaign: utmParams.utm_campaign || '',
          utm_content: utmParams.utm_content || '',
          utm_term: utmParams.utm_term || ''
        });
      }

    } catch (e) {
      console.warn('[SubscribeFlow] Meta Pixel tracking error:', e);
    }
  }
};

  /**
   * Get cookie value by name
   */
  SubscribeFlowManager.prototype.getCookie = function(name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return '';
  };

  /**
   * Send event to Meta Conversion API via AWS Lambda
   */
  SubscribeFlowManager.prototype.sendToConversionAPI = function(eventData) {
    var self = this;

    if (!this.config.metaCapiUrl) {
      if (this.config.metaCapiDebug) {
        console.warn('[SubscribeFlow] Meta CAPI URL not configured, skipping server-side tracking');
      }
      return;
    }

    if (this.config.metaCapiDebug) {
      console.log('[SubscribeFlow] Sending to Meta Conversion API:', {
        event_name: eventData.event_name,
        event_id: eventData.event_id,
        email: eventData.email ? '***@' + eventData.email.split('@')[1] : 'none',
        has_fbp: !!eventData.fbp,
        has_fbc: !!eventData.fbc,
        url: this.config.metaCapiUrl
      });
    }

    // Send to Lambda
    fetch(this.config.metaCapiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData),
      // Use keepalive to ensure sending even if user leaves page
      keepalive: true
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }
      return response.json();
    })
    .then(function(data) {
      if (self.config.metaCapiDebug) {
        console.log('[SubscribeFlow] ✓ Meta Conversion API success:', data);
      }

      // Optional: Track success event
      if (window.MemoketAnalytics) {
        window.MemoketAnalytics.track('meta_capi_success', {
          event_id: eventData.event_id
        });
      }
    })
    .catch(function(error) {
      console.warn('[SubscribeFlow] ⚠️ Meta Conversion API error:', error);

      // Silent failure - don't impact user experience
      // Optional: Track failure event for monitoring
      if (window.MemoketAnalytics) {
        window.MemoketAnalytics.track('meta_capi_error', {
          error: error.message,
          event_id: eventData.event_id
        });
      }
    });
  };

  /**
   * Track state change
   */
  SubscribeFlowManager.prototype.trackStateChange = function(prevState, newState) {
    this.trackEvent('state_change', {
      previous_state: prevState,
      new_state: newState,
      email_present: !!this.email
    });
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      window.subscribeFlow = new SubscribeFlowManager();
    });
  } else {
    window.subscribeFlow = new SubscribeFlowManager();
  }
})();
